<template>
  <div class="narrow-container mx-2">
    <div
      class="d-flex flex-column flex-sm-row gap-3 align-sm-center justify-space-between pa-0 px-4 mt-4 w-full"
    >
      <v-autocomplete
        v-model="selectedItem"
        :items="suggestionApps"
        :search-input.sync="search"
        :loading="autocompleteLoading"
        filled
        dark
        dense
        clearable
        hide-no-data
        hide-details
        prepend-inner-icon="search"
        placeholder="Search by plugin name"
        class="pa-0 flex-grow-0 search-input"
        return-object
        item-text="name"
        item-value="slug"
        @change="onChange"
      ></v-autocomplete>

      <div class="d-flex align-center gap-3">
        <v-select
          dark
          dense
          filled
          clearable
          hide-details
          class="status-filter"
          v-model="filter.status"
          :items="statuses"
          placeholder="Status"
          @change="getReviewsList"
        >
        </v-select>
        <v-btn
          color="primary"
          dark
          @click="openCreateReviewDialog"
          class="ReviewList__add-btn"
        >
          <v-icon left>mdi-plus</v-icon>
          Create Review
        </v-btn>
      </div>
    </div>
    <div class="mt-6 mx-4" v-if="!loading">
      <v-data-table
        :headers="headers"
        :items="reviewList"
        class="data-table"
        sort-by="status"
        :sort-desc="false"
        :custom-sort="customSort"
        show-expand
      >
        <template v-slot:item.plugin="{ item }">
          <router-link
            v-if="item.appObject?.slug"
            :to="{
              name: 'plugin_detail',
              params: { pluginId: item.appObject.slug },
            }"
          >
            {{ item.appObject.name || 'Unknown Plugin' }}
          </router-link>
          <span v-else>{{ item.appObject?.name || 'Unknown Plugin' }}</span>
        </template>
        <template v-slot:item.comment="{ item }">
          <div v-html="item.comment" class="review-comment"></div>
        </template>
        <template v-slot:item.author="{ item }">
          <div>
            {{ item.authorName }} <br />
            {{ item.authorEmail }}
          </div>
        </template>
        <template v-slot:item.rating="{ item }">
          <v-rating
            v-if="item.rating"
            v-model="item.rating"
            background-color="blue lighten-3"
            color="primary"
            small
            dense
            class="ReviewList__rating"
          ></v-rating>
        </template>
        <template v-slot:item.status="{ item }">
          <review-status-chip
            :status="item.status"
            :dataId="item.id.toString()"
            @updateStatus="onUpdateStatus"
          />
        </template>
        <template v-slot:item.updatedAt="{ item }">
          {{ formatDate(item.updatedAt || item.updated_at) }}
        </template>
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length">
            <div v-html="item.comment"></div>
          </td>
        </template>
      </v-data-table>
    </div>
    <div v-else class="spinner">
      <FlamePreloader :compact="true" :size="100" />
    </div>

    <!-- Create Review Dialog -->
    <v-dialog v-model="createReviewDialog" max-width="600px" persistent>
      <v-card dark class="ReviewList__create-review-dialog">
        <v-card-title class="headline">
          Create Review
        </v-card-title>
        <v-card-text>
          <v-form ref="createReviewForm" v-model="createReviewFormValid">
            <v-autocomplete
              v-model="createReviewForm.selectedPlugin"
              :items="createReviewPluginSuggestions"
              :search-input.sync="createReviewPluginSearch"
              :loading="createReviewPluginLoading"
              label="Plugin *"
              required
              :rules="[v => !!v || 'Plugin is required']"
              item-text="name"
              item-value="slug"
              return-object
              filled
              dark
              dense
              clearable
              no-data-text="No plugins found. Start typing to search..."
              placeholder="Search and select a plugin"
              class="mb-4"
              @focus="onPluginAutocompleteFocus"
            >
              <template v-slot:item="{ item }">
                <div>
                  <div>{{ item.name }}</div>
                  <div class="caption text--secondary">{{ item.slug }}</div>
                </div>
              </template>
            </v-autocomplete>

            <v-text-field
              v-model="createReviewForm.authorName"
              label="Author Name *"
              required
              :rules="[v => !!v || 'Author name is required']"
              filled
              dark
              dense
              class="mb-4"
            ></v-text-field>

            <v-text-field
              v-model="createReviewForm.authorEmail"
              label="Author Email *"
              type="email"
              required
              :rules="[
                v => !!v || 'Author email is required',
                v => /.+@.+\..+/.test(v) || 'Email must be valid'
              ]"
              filled
              dark
              dense
              class="mb-4"
            ></v-text-field>

            <div class="mb-4">
              <v-label class="mb-2">Rating *</v-label>
              <v-rating
                v-model="createReviewForm.rating"
                background-color="blue lighten-3"
                color="primary"
                hover
                class="ReviewList__rating"
              ></v-rating>
              <div v-if="!createReviewForm.rating || createReviewForm.rating === 0" class="text-caption error--text mt-1">
                Rating is required
              </div>
            </div>

            <v-textarea
              v-model="createReviewForm.comment"
              label="Comment"
              filled
              dark
              rows="4"
              hint="Optional review comment (max 5000 characters)"
              :rules="[
                v => !v || v.length <= 5000 || 'Comment must be 5000 characters or less'
              ]"
              counter="5000"
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text dark @click="closeCreateReviewDialog">Cancel</v-btn>
          <v-btn
            color="primary"
            dark
            @click="submitCreateReview"
            :disabled="!createReviewFormValid || creatingReview || !isCreateReviewFormReady"
            :loading="creatingReview"
          >
            Create
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapState } from 'vuex'
import dayjs from 'dayjs'
import { MODAL_STATUS_CHANGE_CONFIRMATION } from '@/components/Modals'
import FlamePreloader from '@/components/_Common/FlamePreloader'
import ReviewStatusChip from './ReviewStatusChip'
import marketplaceAPI from '@/services/marketplace'

export default {
  name: 'ReviewList',
  components: {
    ReviewStatusChip,
    FlamePreloader,
  },
  data() {
    return {
      search: '',
      loading: false,
      columns: ['plugin', 'author', 'comment', 'rating', 'status', 'updatedAt'],
      columnOptions: [
        { text: 'Plugin', value: 'plugin', sortable: false },
        { text: 'Author', value: 'author', sortable: false },
        { text: 'Comment', value: 'comment', sortable: false },
        { text: 'Rating', value: 'rating' },
        { text: 'Status', value: 'status' },
        { text: 'Updated At', value: 'updatedAt' },
      ],
      filter: {
        plugin_slug: '',
        status: '',
      },
      statuses: ['pending', 'approved', 'declined'],
      reviewList: [],
      wholeReviewList: [],
      selectedItem: null,
      suggestionApps: [], // your list of items
      autocompleteLoading: false,
      // Create Review Dialog
      createReviewDialog: false,
      createReviewFormValid: false,
      creatingReview: false,
      createReviewForm: {
        selectedPlugin: null,
        authorName: '',
        authorEmail: '',
        rating: null,
        comment: '',
      },
      createReviewPluginSearch: '',
      createReviewPluginSuggestions: [],
      createReviewPluginLoading: false,
      searchTimeout: null,
    }
  },
  computed: {
    ...mapState({
      currentPublisherInfo: ({ publisher_infos }) => publisher_infos.current,
    }),
    // Which columns are to be displayed on the table based on selected columns filter
    // Header rowspan applied are handled by header details
    headers() {
      const { columns, columnOptions } = this
      const options = columnOptions.reduce(
        (acc, cur) =>
          cur.children ? [...acc, ...cur.children] : [...acc, cur],
        []
      )
      return options.filter((option) => columns.indexOf(option.value) !== -1)
    },
    isCreateReviewFormReady() {
      return (
        this.createReviewForm.selectedPlugin &&
        this.createReviewForm.authorName &&
        this.createReviewForm.authorEmail &&
        this.createReviewForm.rating &&
        this.createReviewForm.rating > 0
      )
    },
  },
  async mounted() {
    await this.getReviewsList()
  },
  methods: {
    async getReviewsList() {
      this.loading = true
      try {
        const filters = {}
        if (this.filter.plugin_slug) {
          filters.plugin_slug = this.filter.plugin_slug
        }
        if (this.filter.status) {
          filters.status = this.filter.status
        }
        
        const response = await marketplaceAPI.listAdminReviews(filters)
        // Map API response to component format
        const reviews = (response.reviews || response.data || []).map((review) => ({
          id: review.id,
          authorName: review.author_name,
          authorEmail: review.author_email,
          comment: review.comment || '',
          rating: review.rating,
          status: review.status,
          updatedAt: review.updated_at || review.updatedAt,
          appObject: {
            slug: review.plugin?.slug || review.plugin_slug,
            name: review.plugin?.name || review.plugin_name,
          },
        }))
        this.reviewList = reviews
        this.wholeReviewList = reviews
      } catch (error) {
        console.error('Error in getReviewsList', error)
        this.$store.commit('application/showNotification', {
          message: error.message || 'Failed to load reviews',
          type: 'error',
        })
      }
      this.loading = false
    },

    onUpdateStatus(params) {
      this.$store.commit('application/openModal', {
        component: MODAL_STATUS_CHANGE_CONFIRMATION,
        props: {
          title: 'Are you sure you want to change status?',
          showCancelBtn: true,
          actionBtnLabel: 'Confirm',
          action: () => {
            this.updateReviewStatus(params)
          },
        },
      })
    },
    // Listener of Status Chip component update status
    async updateReviewStatus(params) {
      const { dataId, status } = params
      this.loading = true
      try {
        // Use appropriate API method based on status
        if (status === 'approved') {
          await marketplaceAPI.approveReview(dataId)
        } else if (status === 'declined') {
          await marketplaceAPI.declineReview(dataId)
        } else {
          // For other status updates, use updateAdminReview
          await marketplaceAPI.updateAdminReview(dataId, { status })
        }
        
        // Smart Update instead of fetching review list again
        const reviewIndex = this.reviewList.findIndex(
          (review) => review.id === dataId
        )
        if (reviewIndex !== -1) {
          let review = this.reviewList[reviewIndex]
          review = { ...review, status }
          Vue.set(this.reviewList, reviewIndex, review)
        }
      } catch (error) {
        console.error('Error while Updating Review', error)
        this.$store.commit('application/showNotification', {
          message: error.message || 'Failed to update review status',
          type: 'error',
        })
      }
      this.loading = false
    },

    async updateStatusFilter(params) {
      const { status } = params
      this.filter.status = status
      await this.getReviewsList()
    },
    // Autocomplete item select event handler
    async onChange() {
      this.filter.plugin_slug = this.selectedItem?.slug || ''
      await this.getReviewsList()
    },
    customSort(items, sortFields, sortDesc) {
      return items.sort((a, b) => {
        for (let i = 0; i < sortFields.length; i++) {
          const result = this.customSortByOneField(sortFields[i], a, b)
          if (result !== 0) return sortDesc[i] ? result : -result
        }
        return 0
      })
    },
    customSortByOneField(sortField, a, b) {
      if (sortField === 'rating') return this.ratingSort(a, b)
      if (sortField === 'status') return this.statusSort(a, b)
      if (sortField === 'updatedAt') return this.updatedAtSort(a, b)
      const valueA = a[sortField].toLowerCase(),
        valueB = b[sortField].toLowerCase()
      if (valueA === valueB) {
        return 0
      } else if (valueA > valueB) {
        return -1
      } else {
        return 1
      }
    },
    ratingSort(a, b) {
      const firstRating = a.rating || -1
      const secondRating = b.rating || -1
      if (firstRating === secondRating) return 0
      return secondRating > firstRating ? 1 : -1
    },
    statusSort(a, b) {
      const firstStatusIndex = this.statuses.findIndex(
        (status) => status === a.status
      )
      const secondStatusIndex = this.statuses.findIndex(
        (status) => status === b.status
      )
      if (firstStatusIndex === secondStatusIndex) return 0
      return secondStatusIndex > firstStatusIndex ? 1 : -1
    },
    updatedAtSort(a, b) {
      const firstUpdatedAt = dayjs(a.updatedAt)
      const secondUpdatedAt = dayjs(b.updatedAt)
      if (firstUpdatedAt === secondUpdatedAt) return 0
      return secondUpdatedAt > firstUpdatedAt ? 1 : -1
    },
    formatDate(date) {
      return dayjs(date).format('D MMM YYYY hh:mm')
    },
    openCreateReviewDialog() {
      this.createReviewDialog = true
      // Reset search and load plugins when dialog opens
      this.createReviewPluginSearch = ''
      this.createReviewPluginSuggestions = []
      this.loadCreateReviewPlugins()
    },
    onPluginAutocompleteFocus() {
      // Load plugins if not already loaded when user focuses on the field
      if (this.createReviewPluginSuggestions.length === 0 && !this.createReviewPluginLoading) {
        this.loadCreateReviewPlugins()
      }
    },
    closeCreateReviewDialog() {
      this.createReviewDialog = false
      this.createReviewForm = {
        selectedPlugin: null,
        authorName: '',
        authorEmail: '',
        rating: null,
        comment: '',
      }
      this.createReviewPluginSearch = ''
      this.createReviewPluginSuggestions = []
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
        this.searchTimeout = null
      }
      if (this.$refs.createReviewForm) {
        this.$refs.createReviewForm.resetValidation()
      }
    },
    async loadCreateReviewPlugins(searchQuery = '') {
      if (this.createReviewPluginLoading) return
      this.createReviewPluginLoading = true
      try {
        const filters = {
          status: 'active',
        }
        if (searchQuery && searchQuery.length >= 2) {
          filters.query = searchQuery
        }
        
        const plugins = await marketplaceAPI.listPlugins(filters)
        this.createReviewPluginSuggestions = plugins.map((plugin) => ({
          id: plugin.id,
          slug: plugin.slug,
          name: plugin.name,
        }))
        console.log(`Loaded ${this.createReviewPluginSuggestions.length} plugins for review creation`, {
          searchQuery,
          plugins: this.createReviewPluginSuggestions.slice(0, 5), // Log first 5
        })
      } catch (error) {
        console.error('Error loading plugins for review creation', error)
        this.$store.commit('application/showNotification', {
          message: 'Failed to load plugins',
          type: 'error',
        })
      }
      this.createReviewPluginLoading = false
    },
    async submitCreateReview() {
      if (!this.$refs.createReviewForm.validate()) {
        return
      }

      if (!this.isCreateReviewFormReady) {
        this.$store.commit('application/showNotification', {
          message: 'Please fill in all required fields',
          type: 'error',
        })
        return
      }

      this.creatingReview = true
      try {
        const selectedPlugin = this.createReviewForm.selectedPlugin
        if (!selectedPlugin || !selectedPlugin.slug) {
          throw new Error('Please select a plugin')
        }

        await marketplaceAPI.createReview({
          plugin_slug: selectedPlugin.slug,
          author_name: this.createReviewForm.authorName,
          author_email: this.createReviewForm.authorEmail,
          rating: this.createReviewForm.rating,
          comment: this.createReviewForm.comment || undefined,
        })

        this.$store.commit('application/showNotification', {
          message: 'Review created successfully',
          type: 'success',
        })

        this.closeCreateReviewDialog()
        await this.getReviewsList()
      } catch (error) {
        console.error('Error creating review', error)
        this.$store.commit('application/showNotification', {
          message: error.message || 'Failed to create review',
          type: 'error',
        })
      }
      this.creatingReview = false
    },
  },
  watch: {
    async search() {
      if (this.autocompleteLoading || !this.search || this.search.length < 3)
        return

      this.autocompleteLoading = true
      try {
        const plugins = await marketplaceAPI.listPlugins({
          query: this.search,
          status: 'active',
        })
        // Map plugins to autocomplete format
        this.suggestionApps = plugins.map((plugin) => ({
          id: plugin.id,
          slug: plugin.slug,
          name: plugin.name,
        }))
      } catch (error) {
        console.error('Error in plugin autocomplete', error)
        this.suggestionApps = []
      }
      this.autocompleteLoading = false
    },
    async createReviewPluginSearch() {
      // Debounce: wait a bit before searching
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }

      this.searchTimeout = setTimeout(async () => {
        // If search is cleared, reload all plugins
        if (!this.createReviewPluginSearch || this.createReviewPluginSearch === '') {
          await this.loadCreateReviewPlugins()
          return
        }

        // If search has at least 2 characters, search with query
        if (this.createReviewPluginSearch.length >= 2) {
          await this.loadCreateReviewPlugins(this.createReviewPluginSearch)
        }
      }, 300) // 300ms debounce
    },
  },
}
</script>

<style lang="scss">
.data-table {
  background: $N4 !important;
  border: 1px solid $N5;
  .review-comment {
    width: 150px;
    * {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}
</style>

<style lang="scss" scoped>
.owner {
  background: orange;
  color: white;
  border-radius: 4px;
}

.remove {
  cursor: pointer;
  color: #e94a53;
  border: none;
}
.access-tab {
  background: #1c1b2e;
}

.avatar-placeholder-wrapper {
  height: 26px;
  width: 26px;
  border-radius: 100%;
  border: 1px solid #b3b3b3;
  overflow: hidden;

  .avatar-placeholder {
    height: 24px;
  }
}

$style: ReviewList;
.#{$style} {
  &__filter-button {
    background: transparent !important;
    border: 1px solid $N3;
    span {
      margin-left: 10px;
      margin-right: 15px;
    }
  }
  &__icon-active {
    color: $N2;
    fill: $N2;
  }
  &__icon-inactive {
    color: $N3;
    fill: $N3;
    &.expanded {
      transform: rotate(-180deg);
    }
  }
  &__add-btn {
    background-color: $B1 !important;
  }
}

.dense-item {
  max-height: 40px;
}
.search-input {
  width: 344px;
}
.min-height-0 {
  min-height: 0px;
}
.parent-checkbox {
  max-height: 40px;
  margin-top: 6px;
}
th {
  padding: 10px;
  border-bottom: thin solid hsla(0, 0%, 100%, 0.12);
  &.parent {
    border-left: thin solid hsla(0, 0%, 100%, 0.12);
    border-right: thin solid hsla(0, 0%, 100%, 0.12);
  }
  &.child {
    border-left: thin solid hsla(0, 0%, 100%, 0.12);
    border-right: thin solid hsla(0, 0%, 100%, 0.12);
  }
}
.approved {
  color: $G1;
  &:before {
    background: $G1;
  }
}
.waiting-review {
  color: $Y1;
  &:before {
    background: $Y1;
  }
}
.declined {
  color: $R2;
  &:before {
    background: $R2;
  }
}
.spinner {
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  min-height: 80vh;
}
.developer-filter {
  border: 1px solid #5e636d;
  border-radius: 8px;
  width: 300px;
}
.status-filter {
  width: 200px;
}
.gap-3 {
  gap: 12px;
}

.ReviewList__create-review-dialog {
  &.theme--dark.v-card {
    background: $N4 !important;
    border: 1px solid $N5 !important;
    box-shadow: 0px 20px 60px rgba(0, 0, 0, 0.25) !important;
  }

  .v-card__title {
    color: $white;
    border-bottom: 1px solid $N5;
    padding-bottom: 16px;
    margin-bottom: 16px;
  }

  .v-card__text {
    color: $white;
  }

  .v-card__actions {
    border-top: 1px solid $N5;
    padding-top: 16px;
    margin-top: 16px;
  }
}

.ReviewList__rating {
  .v-icon {
    color: $B1 !important;
  }
}
</style>
